<template>
  <div id="incident-management-report" data-test="outside-vif">
    <b-container fluid>
      <b-container class="raft-charts-wrapper">
        <TotalReportsByAreaType
          id="reports-by-area-type"
          chart-title="Reports by Area Type"
          class="raft-chart"
        />
        <div class="grid--2 view-content">
          <div class="views-row">
            <OpenReportsByStatus
              id="open-reports-by-status"
              chart-title="Open Reports by Status"
              chart-width="50%"
              class="raft-chart"
            />
            <ReportsByReportType
              id="reports-by-report-type"
              chart-title="Reports By Report Type"
              chart-height="100%"
              class="raft-chart"
            />
          </div>
        </div>
        <OpenReportsByDepartment
          id="open-reports-by-department"
          chart-title="Open Reports by Department/Unit"
          class="raft-chart"
        />
        <ReportsByDepartmentYear
          id="open-reports-by-department-year"
          chart-title="Reports in the Last Year by Department/Unit"
          class="raft-chart"
        />
      </b-container>
    </b-container>

    <b-form>
      <fieldset class="incident-report-listing-filters">
        <legend>Incident Report Summary</legend>
        <b-container>
          <b-row>
            <b-col class="raft-js-filter-cols datefilter-wrapper-col">
              <date-filter
                :date-between="true"
                :get-value="reportedAfterDate"
                :update-method="updateDateBetweenStart"
                :end-date-get-value="reportedBeforeDate"
                :end-date-update-method="updateDateBetweenEnd"
                label="Reported After"
                end-label="Reported Before"
                id="date-filter"
              />
            </b-col>
          </b-row>
        </b-container>
        <b-table
          striped
          hover
          show-empty
          id="incident-management-report"
          class="incident-management-report"
          :fields="fields"
          :items="items"
          ref="table"
          v-if="!isBusy"
        >
          <template #cell(value)="data">
            <div v-if="data.item.field === 'near_misses'">
              <b-link
                :href="'/raft/incident-management/incident-reports?reportTypeFilter=near_miss'"
              >
                {{ data.item.value }}
              </b-link>
            </div>
            <div v-else-if="data.item.field === 'incident_reports'">
              <b-link
                :href="'/raft/incident-management/incident-reports?reportTypeFilter=incident_report'"
              >
                {{ data.item.value }}
              </b-link>
            </div>
            <div v-else-if="data.item.field === 'open_reports'">
              <b-link
                :href="'/raft/incident-management/incident-reports?statusFilter=in_progress&statusFilter=collecting_details&statusFilter=submitted'"
              >
                {{ data.item.value }}
              </b-link>
            </div>
            <div v-else>
              <b-link :href="'/raft/incident-management/incident-reports'">
                {{ data.item.value }}
              </b-link>
            </div>
          </template>
          <template #empty>
            <ErrorStateLinks />
          </template>
        </b-table>
        <div class="busy-wrapper">
          <raft-busy v-if="isBusy" busy-text="Loading..." />
        </div>
      </fieldset>
    </b-form>
  </div>
</template>

<script>
import ErrorStateLinks from "../raft/ErrorStateLinks.vue";
import { restClient } from "../../lib/raftApi";
import DateFilter from "../filters/DateFilter.vue";
import OpenReportsByDepartment from "./charts/OpenReportsByDepartment.vue";
import OpenReportsByStatus from "./charts/OpenReportsByStatus.vue";
import ReportsByDepartmentYear from "./charts/ReportsByDepartmentYear.vue";
import ReportsByReportType from "./charts/ReportsByReportType.vue";
import TotalReportsByAreaType from "./charts/TotalReportsByAreaType.vue";
import RaftBusy from "../RaftBusy.vue";

export default {
  name: "IncidentManagementReport",
  inject: ["isB4"],
  components: {
    DateFilter,
    OpenReportsByDepartment,
    OpenReportsByStatus,
    ReportsByDepartmentYear,
    ReportsByReportType,
    TotalReportsByAreaType,
    RaftBusy,
    ErrorStateLinks,
  },
  data() {
    return {
      fields: [
        { key: "title", label: "Item" },
        { key: "value", label: "Count", formatter: this.formatLinks },
      ],
      reportedAfterDate: null,
      reportedBeforeDate: null,
      reportedAfter: null,
      reportedBefore: null,
      response: [],
      isBusy: false,
    };
  },
  async mounted() {
    await this.getReportData();
  },
  computed: {
    items() {
      return this.response && Object.values(this.response);
    },
  },
  methods: {
    async getReportData() {
      this.isBusy = true;
      const queries = {};
      if (this.reportedAfter) {
        queries.start_date = this.reportedAfter;
      }
      if (this.reportedBefore) {
        queries.end_date = this.reportedBefore;
      }

      this.response = await restClient
        .get({ path: "incident-management/reports/summary", queries })
        .catch((e) => {
          this.$showNotification("error", `Unable to load data. ${e.message}.`);
          this.isBusy = false;
        });
      this.isBusy = false;
    },
    updateDateBetweenStart(value) {
      if (value) {
        let date = new Date(value);
        if (date) {
          this.reportedAfterDate = date;
          date =
            date.getFullYear() +
            "-" +
            (date.getMonth() + 1).toString().padStart(2, "0") +
            "-" +
            date.getDate().toString().padStart(2, "0");
        }
        this.reportedAfter = date;
      } else {
        this.reportedAfter = null;
        this.reportedAfterDate = null;
      }
      this.getReportData();
    },
    updateDateBetweenEnd(value) {
      if (value) {
        let date = new Date(value);
        if (date) {
          this.reportedBeforeDate = date;
          date =
            date.getFullYear() +
            "-" +
            (date.getMonth() + 1).toString().padStart(2, "0") +
            "-" +
            date.getDate().toString().padStart(2, "0");
        }
        this.reportedBefore = date;
      } else {
        this.reportedBefore = null;
        this.reportedBeforeDate = null;
      }
      this.getReportData();
    },
  },
};
</script>

<style scoped>
.incident-report-listing-filters {
  margin-top: 12px;
  display: flex;
  flex-direction: column;
}
</style>
